import { UseQueryResult } from '@tanstack/react-query';

import {
  apiFrontendInternalLinkingCityLocaleRetrieve,
  getApiFrontendInternalLinkingCityLocaleRetrieveQueryKey,
  useApiFrontendInternalLinkingCityLocaleRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-internal-linking-city-locale-retrieve';
import {
  apiFrontendInternalLinkingNeighborhoodLocaleRetrieve,
  getApiFrontendInternalLinkingNeighborhoodLocaleRetrieveQueryKey,
  useApiFrontendInternalLinkingNeighborhoodLocaleRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-internal-linking-neighborhood-locale-retrieve';
import {
  apiFrontendInternalLinkingRegionLocaleRetrieve,
  getApiFrontendInternalLinkingRegionLocaleRetrieveQueryKey,
  useApiFrontendInternalLinkingRegionLocaleRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-internal-linking-region-locale-retrieve';
import type { ApiFrontendInternalLinkingCityLocaleRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingCityLocaleRetrieve200';
import type { ApiFrontendInternalLinkingNeighborhoodLocaleRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingNeighborhoodLocaleRetrieve200';
import type { ApiFrontendInternalLinkingRegionLocaleRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingRegionLocaleRetrieve200';
import { ErrorType } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';
import type { Locality } from '@rover/types/src/Locality';
import {
  type CountryUrlPrefix,
  getDefaultShortLanguageByUrlPrefix,
} from '@rover/utilities/roverGeo.constants';

type InternalLinkingResponse =
  | ApiFrontendInternalLinkingRegionLocaleRetrieve200
  | ApiFrontendInternalLinkingCityLocaleRetrieve200
  | ApiFrontendInternalLinkingNeighborhoodLocaleRetrieve200;

// Default-locale URLs omit the language segment, so fall back to the country's
// default language. The locale endpoints are then called unconditionally — the
// default locale is just another locale to them.
const resolveLanguage = (country: string, language?: string): string =>
  language ?? getDefaultShortLanguageByUrlPrefix(country as CountryUrlPrefix);

export const getInternalLinkingPrefetchQueryOptions = ({
  country,
  region,
  city,
  neighborhood,
  language,
}: Locality): {
  queryKey: readonly string[];
  queryFn: () => Promise<InternalLinkingResponse>;
} => {
  const lang = resolveLanguage(country, language);

  if (neighborhood) {
    return {
      queryKey: getApiFrontendInternalLinkingNeighborhoodLocaleRetrieveQueryKey(
        country,
        lang,
        region,
        city,
        neighborhood
      ),
      queryFn: () =>
        apiFrontendInternalLinkingNeighborhoodLocaleRetrieve(
          country,
          lang,
          region,
          city,
          neighborhood
        ),
    };
  }
  if (city) {
    return {
      queryKey: getApiFrontendInternalLinkingCityLocaleRetrieveQueryKey(
        country,
        lang,
        region,
        city
      ),
      queryFn: () => apiFrontendInternalLinkingCityLocaleRetrieve(country, lang, region, city),
    };
  }
  return {
    queryKey: getApiFrontendInternalLinkingRegionLocaleRetrieveQueryKey(country, lang, region),
    queryFn: () => apiFrontendInternalLinkingRegionLocaleRetrieve(country, lang, region),
  };
};

export const useInternalLinking = ({
  country,
  region,
  city,
  neighborhood,
  language,
}: Locality): UseQueryResult<InternalLinkingResponse, ErrorType<unknown>> => {
  const lang = resolveLanguage(country, language);
  const isCity = !!(city && region && !neighborhood);
  const isNeighborhood = !!(city && region && neighborhood);
  const isRegion = !!(!city && region && !neighborhood);

  const cityResponse = useApiFrontendInternalLinkingCityLocaleRetrieve(
    country,
    lang,
    region,
    city!,
    {
      query: { enabled: isCity },
    }
  );
  const neighborhoodResponse = useApiFrontendInternalLinkingNeighborhoodLocaleRetrieve(
    country,
    lang,
    region,
    city!,
    neighborhood!,
    {
      query: { enabled: isNeighborhood },
    }
  );
  const regionResponse = useApiFrontendInternalLinkingRegionLocaleRetrieve(country, lang, region, {
    query: { enabled: isRegion },
  });

  if (isNeighborhood) {
    return neighborhoodResponse;
  }
  if (isCity) {
    return cityResponse;
  }
  return regionResponse;
};
